import { addToCart } from 'analytics/snowplow/events'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import TextButton from 'LuxKit/Button/TextButton'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import type { FlightsClassTypes } from 'constants/flight'
import ModalContext from 'contexts/ModalContext'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { generateMultipleRoomCheckoutItems } from 'lib/checkout/accommodation/cart'
import { generateAccommodationBundleFlightItems } from 'lib/checkout/flights/cart'
import { isRoomsInvalid } from 'lib/search/searchUtils'
import React, { useContext, useMemo } from 'react'
import * as Analytics from 'analytics/analytics'
import type { PackageOptionViewByRoom, LoadingStatusByRoom, PackagesOptionsViewsByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { getCalendarsToBeFetchedByRoom, getDurationPackageOptionsByRoom, getIsLoadingByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import moment from 'moment'
import Group from 'components/utils/Group'
import useCharterFlightJourneysForHotelDates from 'hooks/CharterFlights/useCharterFlightJourneysForHotelDates'
import { addToUserCart } from 'actions/UserCartActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import { useSelectCartOffers } from 'hooks/UserCart/useCartItems'
import BodyText from 'LuxKit/Typography/BodyText'
import { getSeatsLeftFromJourney, useSeatEligiblePassengers } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/utils'
import { useIntl } from 'react-intl'

interface Props extends React.ComponentProps<typeof TextButton> {
  rooms: Array<App.RoomOccupants>
  offer: App.HotelOffer | App.HotelOfferSummary | App.VillaOffer | App.VillaOfferSummary
  checkIn?: string
  checkOut?: string
  duration?: number
  activePackagesByRoom: PackageOptionViewByRoom
  isLoadingByRoom: LoadingStatusByRoom
  calendarsToBeFetchedByRoom: LoadingStatusByRoom
  durationPackagesByRoom: PackagesOptionsViewsByRoom
  loggedIn?: boolean
  currencyCode: string
  isMultiRoomConnected: boolean
  airport?: App.Airport
  disabled?: boolean
  onProceedToBooking: (items: Array<App.Checkout.AnyItem>, connectRooms: boolean, keepBrowsing: boolean) => void
  flightPrice?: number
  flightsSelected: boolean
  charterBundleRoute?: App.CharterBundleRoute
  fareClass: FlightsClassTypes
}

function MultiRoomOfferCheckoutButton(props: Props) {
  const {
    rooms,
    offer,
    checkIn,
    checkOut,
    duration,
    activePackagesByRoom,
    isLoadingByRoom,
    calendarsToBeFetchedByRoom,
    durationPackagesByRoom,
    loggedIn,
    currencyCode,
    isMultiRoomConnected,
    airport,
    disabled,
    children,
    onProceedToBooking,
    flightPrice,
    flightsSelected,
    charterBundleRoute,
    fareClass,
    ...rest
  } = props

  const intl = useIntl()
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const offers = useSelectCartOffers()
  const offerPageDispatch = useOfferPageDispatch()
  const packagesByRoom = useMemo(() => {
    const selectedPackages = { ...activePackagesByRoom }
    Object
      .entries(selectedPackages)
      // filters only rooms without package
      .filter(([, selectedPackage]) => !selectedPackage)
      .forEach(([roomId]) => {
        const isThisRoomLoading = isLoadingByRoom[roomId] || calendarsToBeFetchedByRoom[roomId]
        // If this room has a "loading" state, we select
        // the first available package for it, only for the
        // component to show the loading indicator
        selectedPackages[roomId] = isThisRoomLoading ? durationPackagesByRoom[roomId][0] : undefined
      })

    return selectedPackages
  }, [activePackagesByRoom, isLoadingByRoom, durationPackagesByRoom, calendarsToBeFetchedByRoom])

  const {
    departingJourney: departingCharterFlightJourney,
    returningJourney: returningCharterFlightJourney,
    departingFareFamily: departingCharterFareFamily,
    returningFareFamily: returningCharterFareFamily,
    fetching: charterFlightsFetching,
    searchResultsTimestamp: charterFlightSearchResultsTimestamp,
    providerSearchId: charterFlightSearchId,
    hasAllFareData: hasAllCharterFlightData,
  } = useCharterFlightJourneysForHotelDates({
    charterBundleRoute,
    checkInDate: checkIn,
    checkOutDate: checkOut,
    occupancies: rooms,
    fareClass,
  })

  const awaitingCharterBundleData = !!charterBundleRoute && charterFlightsFetching
  const failedToFetchCharterBundleData = !!charterBundleRoute && !!checkIn && !!checkOut && !awaitingCharterBundleData && !hasAllCharterFlightData

  const seatEligiblePassengers = useSeatEligiblePassengers(rooms)
  const charterSeatsLeft = useMemo(() => {
    const dep = getSeatsLeftFromJourney(departingCharterFlightJourney)
    const ret = getSeatsLeftFromJourney(returningCharterFlightJourney)
    if (typeof dep === 'number' && typeof ret === 'number') return Math.min(dep, ret)
    return typeof dep === 'number' ? dep : (typeof ret === 'number' ? ret : undefined)
  }, [departingCharterFlightJourney, returningCharterFlightJourney])
  const insufficientCharterSeats = !!charterBundleRoute && typeof charterSeatsLeft === 'number' && charterSeatsLeft < seatEligiblePassengers

  const proceedToBook = (keepBrowsing: boolean) => {
    if (!checkIn) return
    const checkOut = moment(checkIn).add(duration, 'days').format(ISO_DATE_FORMAT)
    const dates = { checkIn, checkOut }

    const accommodationItems = generateMultipleRoomCheckoutItems(packagesByRoom, offer, dates, rooms, currencyCode)
    const flightItems = flightsSelected && airport ? generateAccommodationBundleFlightItems({
      rooms,
      airport,
      offer,
      accommodationItems,
      quotedFare: flightPrice,
      charterBundleRoute,
      fareClass,
      departingCharterFlightJourney,
      returningCharterFlightJourney,
      departingCharterFareFamily,
      returningCharterFareFamily,
      charterFlightSearchResultsTimestamp,
      charterFlightSearchId,
    }) : []

    Analytics.trackEvent(addToCart())

    if (charterBundleRoute && flightItems.length > 0) {
      const flightItemsReadyToAdd = flightItems.filter(item => item.flights.length > 0)
      const accommodationItemsAlreadyInCart = accommodationItems.filter(item => offers?.[item.offerId])

      // add to cart, if item is not already in the cart..
      if (accommodationItemsAlreadyInCart.length === 0) {
        dispatch(addToUserCart([...accommodationItems, ...flightItemsReadyToAdd], true, false))
      }
    }

    onProceedToBooking([...accommodationItems, ...flightItems], isMultiRoomConnected, keepBrowsing)
  }

  const onBookNowClick = async(e: React.MouseEvent<HTMLButtonElement>, keepBrowsing: boolean = false) => {
    if (!checkIn) {
      e.preventDefault()

      offerPageDispatch({ type: OfferPageActions.setRoomType, room: undefined })
      offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    } else if (!loggedIn) {
      e.preventDefault()

      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)

      if (result.loggedIn && !isRoomsInvalid(rooms)) {
        proceedToBook(keepBrowsing)
      }
    } else if (!isRoomsInvalid(rooms)) {
      proceedToBook(keepBrowsing)
    }
  }

  if (failedToFetchCharterBundleData) {
    return <BodyText variant="medium" colour="critical">
      {intl.formatMessage({
        id: 'OP4TJJUZJ3',
        defaultMessage: 'Failed to fetch flight data',
        description: 'Error message when flight data cannot be loaded for multi-room booking',
      })}
    </BodyText>
  }

  return <Group direction="vertical" gap={4}>
    <TextButton
      {...rest}
      type="submit"
      onClick={onBookNowClick}
      disabled={disabled || awaitingCharterBundleData || insufficientCharterSeats}
    >
      {children}
    </TextButton>
    {insufficientCharterSeats && (
      <BodyText variant="small" colour="critical">
        {intl.formatMessage({
          id: 'NjprUaH2dR',
          defaultMessage: 'Selected flight class is not available for your dates',
          description: 'Error message when selected flight class has insufficient seats for dates',
        })}
      </BodyText>
    )}
  </Group>
}

export default withOfferPageAndReduxState((state, appState) => ({
  currencyCode: appState.geo.currentCurrency,
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  duration: state.duration,
  rooms: state.rooms,
  offer: state.offer,
  isMultiRoomConnected: state.isMultiRoomConnected,
  isLoadingByRoom: getIsLoadingByRoom(state, appState),
  calendarsToBeFetchedByRoom: getCalendarsToBeFetchedByRoom(state, appState),
  durationPackagesByRoom: getDurationPackageOptionsByRoom(state, appState),
  loggedIn: selectLoggedIn(appState),
  airport: state.airport,
  flightsSelected: state.flights,
  charterBundleRoute: state.charterBundleRoute,
  fareClass: state.fareClass,
}), MultiRoomOfferCheckoutButton)
